"use client";

import { Hand, Leaf, Waves, Wind } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const principles = [{
  icon: Leaf,
  number: "01 · QUALITY",
  title: "Vibrant by Nature",
  description: "Using premium fast-dry inks and high-density film, our prints capture every nuance of your digital design with unmatched color depth and clarity."
}, {
  icon: Wind,
  number: "02 · SPEED",
  title: "Fast by Design",
  description: "Engineered for rapid production, our workflow ensures your custom apparel transfers are ready for heat-press application in record time."
}, {
  icon: Hand,
  number: "03 · DURABILITY",
  title: "Resilient by Craft",
  description: "Built to withstand countless wash cycles, our transfer technology ensures your branding remains sharp, flexible, and crack-resistant on any fabric."
}, {
  icon: Waves,
  number: "04 · VERSATILITY",
  title: "Adaptive ",
  description: "From cotton tees to synthetic performance gear, our prints bond seamlessly to diverse materials, giving you total creative freedom for your brand."
}];
function PrincipleColumn({
  principle,
  index
}: {
  principle: (typeof principles)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const Icon = principle.icon;
  return <div ref={ref} data-index={index} className="relative px-6 py-10 transition-all duration-700 ease-out !bg-[rgba(250,70,22,0)] md:px-8 md:py-0" style={{
    transitionDelay: `${index * 120}ms`,
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)"
  }}>
      <Icon className="lucide lucide-leaf lucide lucide-leaf lucide lucide-leaf lucide lucide-leaf lucide lucide-leaf size-12 mb-8 stroke-[1] text-primary !bg-[rgba(8,7,7,0)]" aria-hidden="true" />
      <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground mb-5 font-default">
        {principle.number}
      </div>
      <h3 className="font-default font-semibold text-[26px] leading-tight text-foreground mb-5">
        {principle.title}
      </h3>
      <p className="text-[15px] leading-relaxed text-muted-foreground font-default">
        {principle.description}
      </p>
    </div>;
}
export default function Advantage() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2744" id="the-dtf-advantage" className="dark text-foreground py-24 overflow-hidden !bg-[rgba(0,33,165,1)] md:py-32 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div ref={headerRef} className="text-center max-w-4xl mx-auto mb-20 md:mb-28 transition-all duration-700 ease-out" style={{
        opacity: headerVisible ? 1 : 0,
        transform: headerVisible ? "translateY(0)" : "translateY(24px)"
      }}>
          <div className="text-[11px] tracking-[0.22em] uppercase mb-8 font-default text-destructive">
            DTF · ADVANTAGE · 04
          </div>
          <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[64px] leading-[1.05] text-foreground">
            Printed{" "}
            <span className="italic text-destructive">
              precisely
            </span>
            .
            <br className="hidden sm:block" /> Delivered{" "}
            <span className="italic text-destructive">instantly</span>.
          </h2>
        </div>

        <div className="bg-card rounded-lg shadow-lg">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-border">
            {principles.map((principle, i) => <div key={principle.title} data-index={i} className={i >= 2 ? "sm:border-t sm:border-border lg:border-t-0" : ""}>
                <PrincipleColumn principle={principle} index={i} />
              </div>)}
          </div>
        </div>
      </div>

      <div ref={imageRef} className="mt-20 md:mt-28 lg:mt-32 transition-all duration-1000 ease-out" style={{
      opacity: imageVisible ? 1 : 0,
      transform: imageVisible ? "translateY(0)" : "translateY(32px)"
    }}>
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/dtf_printing_process.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/dtf_printing_process.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/dtf_printing_process.png-wvc-sizes" alt="Close up of high-quality DTF film transfer showing vibrant ink saturation on fabric" loading="lazy" decoding="async" className="w-full h-[50vh] object-cover !opacity-[0.7] !text-[rgba(250,250,250,1)] !bg-transparent md:h-[65vh] lg:h-[75vh]" src="https://evolving-grouper.10web.cloud/wp-content/uploads/2026/06/IMG_5702.jpeg" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-6">
          <p className="font-default italic text-sm md:text-base text-muted-foreground">
            industrial-grade transfers, engineered for fashion brands and creators worldwide.
          </p>
        </div>
      </div>
    </section>;
}